/**
 * MobileNav - Hamburger menu for mobile navigation.
 *
 * Shows a hamburger icon that expands to reveal navigation links,
 * theme toggle, and auth controls. Only visible below xs breakpoint (400px).
 */

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Menu as MenuIcon, X, BookOpen, Database, Settings, BarChart3 } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';
import { ConnectedThemeToggle } from '@/components/ui/ThemeToggle';
import { AuthNav, type SyncStatus } from '@/features/auth';

export interface MobileNavProps {
  /** Currently active tab */
  activeTab: string;
  /** Selected simulation ID (enables config/results tabs) */
  selectedSimulationId: string | null;
  /** Whether results are available */
  hasResults: boolean;
  /** Current search params to preserve */
  currentSearch: Record<string, unknown>;
  /** Sync status for auth nav */
  syncStatus: SyncStatus;
  /** Callback when sync is clicked */
  onSyncClick: () => void;
}

export function MobileNav({
  activeTab,
  selectedSimulationId,
  hasResults,
  currentSearch,
  syncStatus,
  onSyncClick,
}: MobileNavProps) {
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
    {
      to: '/' as const,
      label: 'How To',
      icon: BookOpen,
      tab: 'howto',
      enabled: true,
    },
    {
      to: '/simulations' as const,
      label: 'Simulations',
      icon: Database,
      tab: 'simulations',
      enabled: true,
    },
    {
      to: selectedSimulationId ? ('/simulation/$simulationId' as const) : ('/simulations' as const),
      params: selectedSimulationId ? { simulationId: selectedSimulationId } : undefined,
      search: selectedSimulationId ? currentSearch : undefined,
      label: 'Configuration',
      icon: Settings,
      tab: 'config',
      enabled: !!selectedSimulationId,
    },
    {
      to: selectedSimulationId ? ('/simulation/$simulationId/results' as const) : ('/simulations' as const),
      params: selectedSimulationId ? { simulationId: selectedSimulationId } : undefined,
      search: selectedSimulationId ? currentSearch : undefined,
      label: 'Results',
      icon: BarChart3,
      tab: 'results',
      enabled: hasResults || !!selectedSimulationId,
    },
  ];

  const closeMenu = () => setIsOpen(false);

  return (
    <div className="xs:hidden">
      {/* Hamburger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="
          -m-2 p-2 text-text-primary transition-colors
          hover:text-action-primary
        "
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        aria-expanded={isOpen}
      >
        {isOpen ? <X className="size-5" /> : <MenuIcon className="size-5" />}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 top-14.25 z-40 bg-black/20" onClick={closeMenu} />

          {/* Menu panel */}
          <div
            className="
              absolute inset-x-0 top-full z-50 animate-fade-in border-b border-border-default bg-bg-elevated shadow-lg
            "
          >
            <nav className="container mx-auto space-y-1 px-4 py-3">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.tab;
                const isDisabled = !item.enabled;

                return (
                  <Link
                    key={item.tab}
                    to={item.to}
                    params={item.params}
                    search={item.search}
                    onClick={closeMenu}
                    className={cn(
                      `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors`,
                      isActive
                        ? 'bg-bg-accent-subtle text-action-primary'
                        : isDisabled
                          ? 'cursor-not-allowed text-text-disabled'
                          : `
                            text-text-secondary
                            hover:bg-bg-muted hover:text-text-primary
                          `,
                    )}
                  >
                    <Icon className="size-4" />
                    {item.label}
                  </Link>
                );
              })}

              {/* Divider */}
              <div className="my-2 border-t border-border-default" />

              {/* Theme toggle and Auth */}
              <div className="flex items-center justify-between px-3 py-2">
                <span className="text-sm text-text-muted">Theme</span>
                <ConnectedThemeToggle />
              </div>

              <div className="px-3 py-2">
                <AuthNav syncStatus={syncStatus} onSyncClick={onSyncClick} />
              </div>
            </nav>
          </div>
        </>
      )}
    </div>
  );
}
